import { Clock, CheckCircle2 } from '@nexdel/core/utils/icons';
import { entityIcons } from '@nexdel/core/utils/entityIcons';
import { useTranslation } from 'react-i18next';
import { useSupplierDashboard } from '@nexdel/core/hooks/use-dashboard';
import { usePageHeader } from '@nexdel/core/hooks/use-page-header';
import { useAuth } from '@nexdel/core/auth';
import { AvatarCircle } from '@nexdel/ui/layout/AvatarCircle';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import welcomeIllustration from '../assets/welcome-coworking.svg';

// Canonical proposition status scheme — keep in sync with Submissions (both apps).
const statusBadge: Record<string, string> = {
  pending:   'crm-badge-gray',
  submitted: 'crm-badge-blue',
  interview: 'crm-badge-purple',
  accepted:  'crm-badge-green',
  signed:    'crm-badge-teal',
  rejected:  'crm-badge-red',
  withdrawn: 'crm-badge-neutral',
};

export default function SupplierDashboard() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { data, isLoading } = useSupplierDashboard();
  const { user } = useAuth();
  usePageHeader({ title: t('dashboard.title') });

  const dateFnsLocale = i18n.language?.startsWith('fr') ? fr : undefined;

  // ── Welcome hero: greeting + a one-line, friendly status summary ──────────
  const firstName = user?.fullName?.trim().split(/\s+/)[0] ?? '';
  const dispatchCount = data?.pendingDispatches?.length ?? 0;
  const summary = dispatchCount === 0
    ? t('dashboard.welcomeCaughtUp')
    : t('dashboard.welcomeHasOne', { a: t('supplierDashboard.summaryDispatches', { count: dispatchCount }) });

  const stats = [
    { label: t('supplierDashboard.activeMandates'), desc: t('supplierDashboard.activeMandatesDesc'),
      value: data?.activeMandates ?? '—', icon: entityIcons.mandate, href: '/mandates' },
    { label: t('supplierDashboard.openRequirements'), desc: t('supplierDashboard.openRequirementsDesc'),
      value: data?.openRequirements ?? '—', icon: entityIcons.requirement, href: '/requirements' },
    { label: t('supplierDashboard.submittedCandidates'), desc: t('supplierDashboard.submittedCandidatesDesc'),
      value: data?.submittedCandidates ?? '—', icon: entityIcons.candidate, href: '/submissions?type=candidate&status=submitted' },
    { label: t('supplierDashboard.pendingPropositions'), desc: t('supplierDashboard.pendingPropositionsDesc'),
      value: data?.pendingPropositions ?? '—', icon: entityIcons.proposition, href: '/submissions?type=candidate&status=pending' },
  ];

  return (
    <div className="h-full overflow-auto">
      <div className="px-6 py-7 lg:px-10 max-w-[1500px] mx-auto space-y-8">

        {/* ── Top bento: hero + KPI cards, then dispatches + propositions ──── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Hero card */}
          <section className="overflow-hidden rounded-2xl border border-border bg-card flex items-center justify-between gap-4 px-6 py-6 sm:px-8">
            <div className="min-w-0">
              <h2 className="text-xl font-semibold tracking-tight text-foreground">
                {firstName ? t('dashboard.welcomeBack', { name: firstName }) : t('dashboard.welcomeBackGeneric')}
              </h2>
              <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{summary}</p>
            </div>
            {/* Decorative illustration (transparent background — sits directly on the card). */}
            <div className="hidden sm:flex shrink-0 items-center justify-center">
              <img
                src={welcomeIllustration}
                alt=""
                aria-hidden="true"
                draggable={false}
                className="h-36 xl:h-48 w-auto select-none pointer-events-none dark:opacity-90"
              />
            </div>
          </section>

          {/* KPI cards (2×2) */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map(s => (
              <button key={s.label} onClick={() => nav(s.href)}
                className="group flex flex-col rounded-2xl border border-border bg-card text-left p-4 sm:p-5 hover:border-primary/30 hover:shadow-sm transition">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground truncate">{s.label}</p>
                  <s.icon size={14} className="shrink-0 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                </div>
                <p className="mt-2 text-3xl font-semibold tracking-tight tabular-nums leading-none text-foreground">{s.value}</p>
                <p className="mt-2 text-xs text-muted-foreground leading-snug">{s.desc}</p>
              </button>
            ))}
          </div>

          {/* Awaiting your response */}
          <section className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">{t('supplierDashboard.pendingDispatches')}</h3>
              {dispatchCount > 0 && (
                <span className="font-mono text-xs tabular-nums text-muted-foreground bg-secondary rounded-full px-1.5 py-0.5 leading-none">{dispatchCount}</span>
              )}
            </div>
            {isLoading ? (
              <p className="text-xs text-muted-foreground py-6">{t('common.loading')}</p>
            ) : dispatchCount === 0 ? (
              <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
                <CheckCircle2 size={15} className="text-primary/70 shrink-0" />
                {t('common.empty')}
              </div>
            ) : (
              <div className="space-y-0.5">
                {(data?.pendingDispatches ?? []).map(d => (
                  <button
                    key={d.mandateId}
                    onClick={() => nav(`/mandates/${d.mandateId}`)}
                    className="flex items-center gap-2.5 w-full text-left py-2 px-2 -mx-2 rounded-md hover:bg-secondary/40 transition"
                  >
                    <entityIcons.mandate size={13} className="text-muted-foreground mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{d.mandateName}</p>
                      {d.dispatchedAt && (
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          <Clock size={10} />
                          {formatDistanceToNow(new Date(d.dispatchedAt), { addSuffix: true, locale: dateFnsLocale })}
                        </p>
                      )}
                    </div>
                    <span className="crm-badge-amber shrink-0">{t('dispatch.status.sent')}</span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Recent propositions */}
          <section className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold tracking-tight text-foreground">{t('supplierDashboard.recentPropositions')}</h3>
            </div>
            {isLoading ? (
              <p className="text-xs text-muted-foreground py-6">{t('common.loading')}</p>
            ) : !data?.recentPropositions.length ? (
              <p className="text-xs text-muted-foreground py-6">{t('common.empty')}</p>
            ) : (
              <div className="space-y-0.5">
                {data.recentPropositions.map(p => (
                  <div key={p.id} className="flex items-center gap-2.5 py-2 px-2 -mx-2">
                    <AvatarCircle initials={(p.candidateName ?? '?').slice(0, 2).toUpperCase()} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-foreground truncate">{p.candidateName}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.mandateName}</p>
                    </div>
                    <span className={`${statusBadge[p.status] ?? 'crm-badge-gray'} shrink-0`}>
                      {t(`propositions.statuses.${p.status}`, { defaultValue: p.status })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}
